import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Container, GlassCard, CardMotion } from "@/components/site/Primitives";
import { loadLeaderBySlug, loadLeaders, loadInsights } from "@/lib/content-loader";
import { pageUrl } from "@/lib/seo/meta";

export const Route = createFileRoute("/leadership/$slug")({
  loader: async ({ params }) => {
    const [leader, leaders, articles] = await Promise.all([
      loadLeaderBySlug(params.slug),
      loadLeaders(),
      loadInsights(),
    ]);
    if (!leader) throw notFound();
    return { leader, leaders, articles };
  },
  head: ({ loaderData, params }) => {
    const l = loaderData?.leader;
    const profileUrl = pageUrl(`/leadership/${params.slug}`);
    const title = l ? `${l.name} — ${l.designation} | KS Lau & Co` : "Leadership — KS Lau & Co";
    const desc = l
      ? `${l.name}, ${l.designation} at KS Lau & Co. ${l.qualifications}.`
      : "Leadership team at KS Lau & Co.";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "profile" },
        { property: "og:url", content: profileUrl },
        ...(l ? [{ property: "og:image", content: l.image }, { name: "twitter:image", content: l.image }] : []),
      ],
      scripts: l
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Person",
                name: l.name,
                jobTitle: l.designation,
                image: l.image,
                worksFor: { "@type": "Organization", name: "KS Lau & Co" },
                memberOf: l.memberships.map((m) => ({ "@type": "Organization", name: m })),
                url: profileUrl,
              }),
            },
          ]
        : [],
    };
  },
  component: LeaderPage,
  notFoundComponent: () => (
    <div className="container-page py-40 text-center">
      <h1 className="text-2xl font-bold text-primary">Profile not found</h1>
      <Link to="/about" hash="leadership" className="mt-4 inline-block text-base font-semibold text-primary hover:underline">
        ← Back to leadership
      </Link>
    </div>
  ),
  errorComponent: ({ reset }) => (
    <div className="container-page py-40 text-center">
      <h1 className="text-2xl font-bold text-primary">Something went wrong</h1>
      <button onClick={reset} className="mt-4 text-base font-semibold text-primary hover:underline">Try again</button>
    </div>
  ),
});

function LeaderPage() {
  const { leader: l, leaders, articles } = Route.useLoaderData() as {
    leader: Leader;
    leaders: Leader[];
    articles: { slug: string; title: string; category: string; author: { slug: string } }[];
  };
  const authored = articles.filter((a) => a.author.slug === l.slug);
  const others = leaders.filter((x) => x.slug !== l.slug);

  return (
    <>
      {/* Hero — image left, identity right */}
      <section className="relative border-b border-border bg-white">
        <Container>
          <div className="pb-6 pt-8 md:pb-8 md:pt-10">
            <Link
              to="/about"
              hash="leadership"
              className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground hover:text-primary"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Leadership
            </Link>

            <div className="mt-6 grid gap-8 md:grid-cols-[minmax(220px,280px)_1fr] md:gap-10">
              <figure className="overflow-hidden ring-1 ring-primary/10">
                <img
                  src={l.image}
                  alt={`Portrait of ${l.name}`}
                  width={800}
                  height={1000}
                  className="aspect-[4/5] w-full object-cover"
                />
              </figure>

              <div className="flex flex-col justify-center">
                <h1 className="text-3xl font-semibold leading-tight tracking-tight text-primary md:text-4xl lg:text-5xl">
                  {l.name}
                </h1>
                
                <p className="mt-1 text-base font-semibold uppercase tracking-[0.14em] text-gold">
                  {l.designation}
                </p>

              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Body — biography + sidebar */}
      <section className="relative pb-24 pt-12 md:pb-32 md:pt-16">
        <Container>
          <div className="grid gap-12 lg:grid-cols-[1fr_320px] lg:gap-16">
            <article className="max-w-prose">
              <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Biography
              </h2>
              <div className="mt-5 space-y-5 text-justify text-lg leading-relaxed text-foreground/85 md:text-[17px]">
                {l.bio.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </article>

            <aside className="space-y-8">
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Memberships &amp; credentials
                </h2>
                <ul className="mt-4 space-y-2.5">
                  {l.memberships.map((m) => (
                    <li key={m} className="flex gap-3 text-base leading-relaxed text-foreground/85">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                      <span>{m}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {authored.length > 0 && (
                <div>
                  <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    Published insights
                  </h2>
                  <ul className="mt-4 space-y-3">
                    {authored.map((a) => (
                      <li key={a.slug}>
                        <Link
                          to="/insights/$slug"
                          params={{ slug: a.slug }}
                          className="group block border border-border bg-white p-4 transition hover:border-primary/40"
                        >
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-gold">
                            {a.category}
                          </p>
                          <p className="mt-1 text-base font-semibold leading-snug text-primary group-hover:underline">
                            {a.title}
                          </p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </aside>
          </div>

          {/* Other partners */}
          {others.length > 0 && (
            <div className="mt-20 border-t border-border pt-12">
              <h2 className="text-xl font-semibold tracking-tight text-primary md:text-2xl">
                Other partners
              </h2>
              <div className="mt-6 grid grid-cols-2 gap-4 sm:gap-5 lg:grid-cols-5 lg:gap-6">
                {others.map((o, i) => (
                  <Link
                    key={o.slug}
                    to="/leadership/$slug"
                    params={{ slug: o.slug }}
                    className="group block h-full"
                  >
                    <CardMotion
                      animateIndex={i}
                      className="group flex h-full flex-col overflow-hidden border border-border bg-white transition hover:border-primary/40"
                    >
                    <img
                      src={o.image}
                      alt={`Portrait of ${o.name}`}
                      loading="lazy"
                      width={400}
                      height={500}
                      className="aspect-[4/5] w-full object-cover"
                    />
                    <div className="flex flex-1 flex-col p-4">
                      <h3 className="text-base font-semibold tracking-tight text-primary lg:text-lg">{o.name}</h3>
                      <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-gold lg:text-sm">
                        {o.designation}
                      </p>
                      <p className="mt-auto pt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary transition group-hover:gap-2">
                        View profile <ArrowRight className="h-3.5 w-3.5" />
                      </p>
                    </div>
                    </CardMotion>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="mt-16">
            <GlassCard hover={false} className="p-8 md:p-10">
              <div className="grid items-center gap-5 md:grid-cols-[1.5fr_auto]">
                <div>
                  <h3 className="text-xl font-semibold tracking-tight text-primary">
                    Start a conversation
                  </h3>
                  <p className="mt-2 text-base text-muted-foreground">
                    Reach our team to discuss audit, tax or advisory engagements.
                  </p>
                </div>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-primary px-5 py-3 text-base font-semibold text-primary-foreground hover:bg-primary/90 md:justify-self-end"
                >
                  Make an enquiry <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </GlassCard>
          </div>
        </Container>
      </section>
    </>
  );
}
